import * as mocks from '@/api/mocks';

import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  ApolloProvider,
  DefaultContext,
  FetchResult,
  GraphQLRequest,
  InMemoryCache,
  NextLink,
  NormalizedCacheObject,
  Observable,
  Operation,
  createHttpLink,
  from,
} from '@apollo/client';
import { Datetime, deleteCookies, getApiUrl, getCookies, getErrors, handleError, minutesToOffset } from '../../utils';
import { ErrorResponse, onError } from '@apollo/client/link/error';

import { ApolloProviderProps } from '@apollo/client/react/context';
import { GraphApiMockResponse } from '../../api/core';
import { GraphQLFormattedError } from 'graphql';
import { NetworkError } from '@apollo/client/errors';
import { RetryLink } from '@apollo/client/link/retry';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import platform from 'platform';
import { GRAPHQL_RESPONSE_EVENT } from '@/constants';
import { setContext } from '@apollo/client/link/context';
import { triggerEvent } from '@/hooks/useEvent';
import useModals from '@/state/modals';

/* Request Endpoint
  Dynamically set the API URL based on the environment.
*/
const httpLink = createHttpLink({
  uri: (operation: Operation): string => `${getApiUrl('api')}/graphql?${operation?.operationName ?? 'Unknown'}`,
  credentials: 'include',
});

/* Session Link
  If there's no token, don't forward the operation to the network. Instead,
  show the `sessionSignedOut` modal (once) so the user can acknowledge and
  then be signed out. Returning an immediate empty result avoids surfacing
  network errors for every in-flight request when the session expires.
*/
const sessionLink = new ApolloLink((operation: Operation, forward: NextLink): Observable<FetchResult<Record<string, any>>> => {
  const { token } = getCookies('token');
  if (!token) {
    // Allow login events as those will always have no token
    if (operation.operationName === 'Login') return forward(operation);
    // logout(true);
  }
  return forward(operation);
});

/* Request Authentication
  Here we initialize the headers and context for our requests in the Apollo Client.
*/
const authLink = setContext(
  (request: GraphQLRequest<Record<string, any>>, { headers, ...previousContext }: DefaultContext): DefaultContext => {
    const { token } = getCookies('token');
    if (!token) {
      console.warn(`AuthLink: missing token for operation: ${request?.operationName || 'unknown'}`);
    }
    const authHeaders = token ? { authorization: `Bearer ${token}` } : {};
    const commonHeaders = {
      'sky-language': navigator.language || 'unknown',
      'sky-app': 'portal',
      'sky-platform': platform.name || 'unknown',
      'sky-platform-version': platform.version || 'unknown',
      'sky-device-type': platform.os.toString() || 'unknown',
      'sky-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'sky-timezone-offset': minutesToOffset(new Datetime().utcOffset()),
    };
    return {
      ...previousContext,
      headers: { ...headers, ...commonHeaders, ...authHeaders },
    };
  }
);

const showLoggedOutModal = (): void => {
  try {
    useModals
      .getState()
      .setState((current: Record<string, unknown> = {}): Record<string, unknown> => ({ sessionSignedOut: {}, ...current }));
    // remove token on session-related GQL error so reload re-evaluates auth
    deleteCookies('token');
  } catch (err) {
    console.error('Failed to show sessionSignedOut modal', err);
  }
};
/* Request Error Handling
  Here we handle all errors; both GraphQL and Network Errors.
  If it was a Network Error, This is only hit after the request finishes its retries.
*/
const errorLink = onError((error: ErrorResponse): void => {
  const { networkError, graphQLErrors, operation } = error;
  const { operationName } = operation || {};
  const { token } = getCookies('token');
  if (!token || operationName === 'GetAvailabilityCount') return;
  if (graphQLErrors?.some((err: GraphQLFormattedError): boolean => err?.extensions?.code === 1016)) {
    showLoggedOutModal();
    return;
  }
  if (!networkError) return;
  // If we have an actual networkError, then something went wrong on the browser/server level.
  switch (networkError?.['statusCode']) {
    // Unauthorized: Log the user out.
    case undefined: // Getting here with an undefined statusCode means that it has already been retried multiple times.
    case 401: {
      try {
        showLoggedOutModal();
      } catch (err) {
        console.error('Failed to show sessionSignedOut modal', err);
      }
      break;
    }

    // Some other network error: handle it in the default case.
    default: {
      const networkErrors = [{ message: networkError?.message, statusCode: networkError?.['statusCode'] }];
      triggerEvent(GRAPHQL_RESPONSE_EVENT, { operationName, variables: operation?.variables, errors: networkErrors });
      handleError(error);
      break;
    }
  }
});

const handleErrorsLink = new ApolloLink((operation: Operation, forward: NextLink): Observable<FetchResult<Record<string, any>>> => {
  const { operationName, variables } = operation;
  return forward(operation).map((data: FetchResult<Record<string, any>>): FetchResult<Record<string, any>> => {
    try {
      // If we've already shown the session-signed-out modal, avoid surfacing
      // additional error notifications for in-flight requests (e.g. 401s).
      const modalsStore = useModals.getState();
      const isSessionSignedOut = (modalsStore && (modalsStore.state as Record<string, any>)?.sessionSignedOut) || false;
      if (isSessionSignedOut || operationName === 'GetAvailabilityCount') return data;
      const errors = getErrors(data);
      triggerEvent(GRAPHQL_RESPONSE_EVENT, { operationName, variables, data: data?.data, errors });
      const payload = {
        data: data?.data || data,
        errors: errors.length > 0 ? errors : undefined,
        variables: variables || {},
        operationName,
      };
      const options = {
        notification: {
          title: operationName,
        },
      };
      handleError(payload, options);
    } catch (err) {
      handleError(err);
    }
    return data;
  });
});

/* Request Conditional Retry
  This type of link is only used for Network Errors.
*/
const initialDelay = 1000;
const maxDelay = 30000;
// Calculate the maximum number of retries based on the initial and max delay values.
const maxRetries = Math.ceil(Math.log(maxDelay / initialDelay) / Math.log(2));
const retryLink = new RetryLink({
  delay: {
    initial: initialDelay,
    max: maxDelay,
    jitter: false, // Disable jitter to ensure consistent retry intervals.
  },
  attempts: {
    max: maxRetries,
    retryIf: (networkError: NetworkError): boolean => {
      // Using a switch/case here so that we can expand on this concept in the future.
      // For example, if we want to retry on a 401 response, we can add it here.
      const status = networkError?.['statusCode'];
      switch (status) {
        case undefined: {
          // Something weird is happening, let's retry before logging the user out.
          console.warn(`Network Error (${status}): Retrying...`);
          return true;
        }
        case 502: {
          // Server returned a bad gateway; allow retrying before surfacing errors.
          console.warn(`Network Error (${status}): Retrying (502)...`);
          return true;
        }
        default:
          return false;
      }
    },
  },
});

/* Local Link
  This link checks if the request exists in our mocks before returning the result.
  If it doesn't exist, it will continue down the link like normal.
*/
const localLink = new ApolloLink((operation: Operation, forward: NextLink): Observable<FetchResult<Record<string, any>>> => {
  if (window.location.host === 'localhost:5000') {
    const { query, variables } = operation;
    const match = Object.values(mocks)
      .sort((a: GraphApiMockResponse<TypedDocumentNode>) => (a?.request?.variables !== undefined ? -1 : 0))
      .find((mock: any): boolean => {
        const { query: mockedQuery, variables: checkVariables } = mock?.['request'] || {};
        const operationName = query?.definitions?.[0]?.['name']?.['value'];
        return mockedQuery && operationName && mockedQuery === operationName && (checkVariables === undefined || checkVariables(variables));
      });
    if (match) return Observable.of(match?.['result']);
  }
  return forward(operation);
});

/* Cache Object
  Setup the cache for Apollo.
*/
export const cache = new InMemoryCache({
  typePolicies: {
    AirportHasUser: { keyFields: false },
    SearchTripsResponse: { merge: true },
    TripConnection: { merge: true },
    TripTableConnection: { merge: true },
    SearchImportsResponse: { merge: true },
    ImportConnection: { merge: true },
  },
});

/* Apollo Client
  Setup the Apollo Client with the links and cache from above.
*/
export const apolloClient = new ApolloClient<NormalizedCacheObject>({
  link: from([localLink, sessionLink, authLink, errorLink, handleErrorsLink, retryLink, httpLink]),
  cache,
  connectToDevTools: true,
});

/* Apollo Root
  Setup the Apollo Provider for use in the App.
*/
const ApolloRoot = (props: Omit<ApolloProviderProps<InMemoryCache>, 'client'>): JSX.Element => (
  <ApolloProvider {...props} client={apolloClient} />
);

// TODO: When GraphApi migration is finished, remove this.
export const handleOnError = ({ networkError, graphQLErrors }: ApolloError): void =>
  handleError({
    errors: graphQLErrors.length ? graphQLErrors : undefined,
    networkError,
  });

export default ApolloRoot;
